import { BuildingInfo, ListParams, Pico3, Sensor } from "01Data/DataTypes";
import { Model } from "99Tools/useModel";
import { TypeKizaiKind } from "99Tools/utils";
import API from "./API";

export class SelectedItem {
  private work: BuildingInfo | Pico3 | Sensor | undefined;
  private prev: BuildingInfo | Pico3 | Sensor | undefined;
  public readonly index: number = -1;
  constructor(index?: number, data?: BuildingInfo | Pico3 | Sensor) {
    if (index === undefined || data === undefined) {
      this.index = -1;
      this.work = undefined;
      return;
    }
    this.index = index;
    this.work = data.Copy();
    this.prev = data.Copy();
  }
  public IsSelected() {
    return 0 <= this.index;
  }
  public get building() {
    return this.work as BuildingInfo;
  }
  public get pico3() {
    return this.work as Pico3;
  }
  public get sensor() {
    return this.work as Sensor;
  }
  public get buildingPrev() {
    return this.prev as BuildingInfo;
  }
  public get pico3Prev() {
    return this.prev as Pico3;
  }
  public get sensorPrev() {
    return this.prev as Sensor;
  }
}

class PageLocations extends Model {
  public bShowRegisterDlg = false;
  public list = new ListParams();
  public selected = new SelectedItem();
  public reset() {
    this.bShowRegisterDlg = false;
    this.list.reset();
    this.selected = new SelectedItem();
  }
}

class PageDevices extends Model {
  public bShowRegisterDlg = false;
  public building = new BuildingInfo(null);
  public pico3: Pico3[] = [];
  public LTE: Sensor[] = [];
  public listPico3 = new ListParams();
  public listLTE = new ListParams();
  public selectedPico3 = new SelectedItem();
  public selectedLTE = new SelectedItem();
  public reset() {
    this.bShowRegisterDlg = false;
    this.building = new BuildingInfo(null);
    this.pico3 = [];
    this.LTE = [];
    this.listPico3.reset();
    this.listLTE.reset();
    this.selectedPico3 = new SelectedItem();
    this.selectedLTE = new SelectedItem();
  }
}

class PageRegisterDevice extends Model {
  public kind: TypeKizaiKind = "init";
  public deviceId = "";
  public building = new BuildingInfo(null);
  public reset() {
    this.kind = "init";
    this.building = new BuildingInfo(null);
  }
}

class PagePico3Devices extends Model {
  public building = new BuildingInfo(null);
  public pico3 = new Pico3(null);
  public sensors: Sensor[] = [];
  public list = new ListParams();
  public selected = new SelectedItem();
  public reset() {
    this.list.reset();
    this.selected = new SelectedItem();
    this.building = new BuildingInfo(null);
    this.pico3 = new Pico3(null);
    this.sensors = [];
  }
}

export class Locations {
  public locations: BuildingInfo[] = [];
  public pico3: Pico3[] = [];
  public sensors: Sensor[] = [];

  public pageLocations = new PageLocations();
  public pageDevices = new PageDevices();
  public pageRegisterDevice = new PageRegisterDevice();
  public pagePico3Devices = new PagePico3Devices();

  public errorMessage = "";

  /**
   * ページ遷移前にリセットする
   */
  public ResetPageData() {
    this.pageLocations.reset();
    this.pageDevices.reset();
    this.pageRegisterDevice.reset();
    this.pagePico3Devices.reset();
    this.errorMessage = "";
  }

  public SetError(err: any, msg: string) {
    this.errorMessage = msg;
    // console.log(this.errorMessage);
    // console.error(err);
  }

  public async GetLocationData() {
    const data = await API.GetBuildings();
    this.locations = data.map((item: any) => {
      return new BuildingInfo(item);
    });
  }

  public async GetLocationAndDeviceData() {
    const data = await API.GetBuildingPico3Sensors();
    this.locations = data.buildings.map((item: any) => {
      return new BuildingInfo(item);
    });
    this.pico3 = data.pico3.map((item: any) => {
      return new Pico3(item);
    });
    this.sensors = data.sensors.map((item: any) => {
      return new Sensor(item);
    });
  }

  public SearchLocation(id: string) {
    return this.locations.findIndex(
      (location: BuildingInfo) => location.BuildingId === id
    );
  }

  public SearchPico3(id: string) {
    return this.pico3.findIndex((pico3: Pico3) => pico3.PicoId === id);
  }

  public GetPico3List(buildingId: string) {
    return this.pico3.filter((item: Pico3) => item.BuildingId === buildingId);
  }

  public GetPico3SensorList(buildingId: string, pico3Id: string) {
    return this.sensors.filter(
      (item: Sensor) =>
        item.BuildingId === buildingId && item.PicoId === pico3Id
    );
  }

  public GetLTEList(buildingId: string) {
    return this.sensors.filter(
      (item: Sensor) =>
        (item.SensorType === "LTE" || item.SensorType === "SubGiga") &&
        item.BuildingId === buildingId
    );
  }

  public async RegisterLocation(
    name: string,
    syokenId: string,
    address: string,
    image: string,
    imageExtension: string
  ) {
    await API.RegisterBuilding(name, syokenId, address, image, imageExtension);
  }

  public async ResigterPico3(
    id: string,
    name: string,
    buildingId: string,
    locationName: string,
    image: string,
    imageExtension: string
  ) {
    let body: string[] = [];
    if (id !== "") {
      body.push(`"PicoId":"${id}"`);
    }
    if (name !== "") {
      body.push(`"PicoName":"${name}"`);
    }
    if (buildingId !== "") {
      body.push(`"BuildingId":"${buildingId}"`);
    }
    if (locationName !== "") {
      body.push(`"LocationName":"${locationName}"`);
    }
    if (image !== "") {
      body.push(`"PicoImage":"${image}"`);
    }
    if (imageExtension !== "") {
      body.push(`"PicoImageExtension":"${imageExtension}"`);
    }
    if (body.length === 0) {
      return;
    }
    await API.RegisterPico3(`{${body.join(",")}}`);
  }

  public async ResigterSensor(
    id: string,
    name: string,
    buildingId: string,
    locationName: string,
    image: string,
    imageExtension: string
  ) {
    let body: string[] = [];
    if (id !== "") {
      body.push(`"SensorId":"${id}"`);
    }
    if (name !== "") {
      body.push(`"SensorName":"${name}"`);
    }
    if (buildingId !== "") {
      body.push(`"BuildingId":"${buildingId}"`);
    }
    if (locationName !== "") {
      body.push(`"LocationName":"${locationName}"`);
    }
    if (image !== "") {
      body.push(`"SensorImage":"${image}"`);
    }
    if (imageExtension !== "") {
      body.push(`"SensorImageExtension":"${imageExtension}"`);
    }
    if (body.length === 0) {
      return;
    }
    await API.RegisterSensor(`{${body.join(",")}}`);
  }

  public async DeleteLocation() {
    if (!this.pageLocations.selected.IsSelected()) {
      return;
    }
    const b = this.pageLocations.selected.building;
    await API.DeleteBuilding(b.BuildingId);
  }

  public async DeletePico3() {
    if (!this.pageDevices.selectedPico3.IsSelected()) {
      return;
    }
    const p = this.pageDevices.selectedPico3.pico3;
    await API.DeletePico3(p.PicoId);
  }

  public async DeleteLTE() {
    if (!this.pageDevices.selectedLTE.IsSelected()) {
      return;
    }
    const l = this.pageDevices.selectedLTE.sensor;
    await API.DeleteSensor(l.SensorId);
  }

  public async EditLocation() {
    if (!this.pageLocations.selected.IsSelected()) {
      return;
    }
    const b = this.pageLocations.selected.building;
    const bPrev = this.pageLocations.selected.buildingPrev;
    let body: string[] = [];
    if (b.BuildingName !== bPrev.BuildingName) {
      body.push(`"BuildingName":"${b.BuildingName.replaceAll(`"`, `\\"`)}"`);
    }
    if (b.PolicyNumber !== bPrev.PolicyNumber) {
      body.push(`"PolicyNumber":"${b.PolicyNumber}"`);
    }
    if (b.BuildingAddress !== bPrev.BuildingAddress) {
      body.push(
        `"BuildingAddress":"${b.BuildingAddress.replaceAll(`"`, `\\"`)}"`
      );
    }
    if (b.ImageEncoded !== bPrev.ImageEncoded) {
      body.push(`"BuildingImage":"${b.ImageEncoded}"`);
    }
    if (b.ImageExtension !== bPrev.ImageExtension) {
      body.push(`"BuildingImageExtension":"${b.ImageExtension}"`);
    }
    if (body.length === 0) {
      return;
    }
    await API.EditBuilding(b.BuildingId, `{${body.join(",")}}`);
  }

  public async EditPico3() {
    if (!this.pageDevices.selectedPico3.IsSelected()) {
      return;
    }
    const p = this.pageDevices.selectedPico3.pico3;
    const pPrev = this.pageDevices.selectedPico3.pico3Prev;
    let body: string[] = [];
    if (p.PicoName !== pPrev.PicoName) {
      body.push(`"PicoName":"${p.PicoName.replaceAll(`"`, `\\"`)}"`);
    }
    if (p.LocationName !== pPrev.LocationName) {
      body.push(`"LocationName":"${p.LocationName.replaceAll(`"`, `\\"`)}"`);
    }
    if (p.ImageEncoded !== pPrev.ImageEncoded) {
      body.push(`"PicoImage":"${p.ImageEncoded}"`);
    }
    if (p.ImageExtension !== pPrev.ImageExtension) {
      body.push(`"PicoImageExtension":"${p.ImageExtension}"`);
    }
    if (body.length === 0) {
      return;
    }
    await API.EditPico3(p.PicoId, `{${body.join(",")}}`);
  }

  public async EditLTE() {
    if (!this.pageDevices.selectedLTE.IsSelected()) {
      return;
    }
    const l = this.pageDevices.selectedLTE.sensor;
    const lPrev = this.pageDevices.selectedLTE.sensorPrev;
    let body: string[] = [];
    if (l.SensorName !== lPrev.SensorName) {
      body.push(`"SensorName":"${l.SensorName.replaceAll(`"`, `\\"`)}"`);
    }
    if (l.NoticeInterval !== lPrev.NoticeInterval) {
      body.push(`"NoticeInterval":"${l.NoticeInterval}"`);
    }
    if (l.LocationName !== lPrev.LocationName) {
      body.push(`"LocationName":"${l.LocationName.replaceAll(`"`, `\\"`)}"`);
    }
    if (l.ImageEncoded !== lPrev.ImageEncoded) {
      body.push(`"SensorImage":"${l.ImageEncoded}"`);
    }
    if (l.ImageExtension !== lPrev.ImageExtension) {
      body.push(`"SensorImageExtension":"${l.ImageExtension}"`);
    }
    if (body.length === 0) {
      return;
    }
    await API.EditSensor(l.SensorId, `{${body.join(",")}}`);
  }

  public async EditPico3Sensor() {
    if (!this.pagePico3Devices.selected.IsSelected()) {
      return;
    }
    const l = this.pagePico3Devices.selected.sensor;
    const lPrev = this.pagePico3Devices.selected.sensorPrev;
    let body: string[] = [];
    if (l.SensorName !== lPrev.SensorName) {
      body.push(`"SensorName":"${l.SensorName.replaceAll(`"`, `\\"`)}"`);
    }
    if (l.NoticeInterval !== lPrev.NoticeInterval) {
      body.push(`"NoticeInterval":"${l.NoticeInterval}"`);
    }
    if (l.LocationName !== lPrev.LocationName) {
      body.push(`"LocationName":"${l.LocationName.replaceAll(`"`, `\\"`)}"`);
    }
    if (l.ImageEncoded !== lPrev.ImageEncoded) {
      body.push(`"SensorImage":"${l.ImageEncoded}"`);
    }
    if (l.ImageExtension !== lPrev.ImageExtension) {
      body.push(`"SensorImageExtension":"${l.ImageExtension}"`);
    }
    if (body.length === 0) {
      return;
    }
    await API.EditSensor(l.SensorId, `{${body.join(",")}}`);
  }
}
